<template>
  <div
    class="chat-window"
    :style="ReadOnly ? 'height: auto; width: 100%;' : ''"
  >
    <div
      class="chat-messages"
      id="chatArea"
      :style="ReadOnly ? 'overflow-y: visible;' : ''"
    >
      <section v-for="item in messages" :key="item.id">
        <span v-if="item.order.client.clientGroup">
          <span v-if="currentUser !== item.from.name">
            <span
              v-if="item.from.role === 'ADMIN'"
              class="sender"
            >
              {{ admin }}
              {{ item.from.name }}
            </span>
            <span
              v-else
              class="sender"
            >
              {{ item.order.client.clientGroup.name }}
              {{ item.from.name }}
            </span>
          </span>
        </span>
        <div id="messages">
          <AdminChatBaloon
            :headerRight="item.createdAt | humanize.date"
            :body="item.body"
            :files="item.files"
            :images="item.images"
            :currentUser="currentUser"
            :user="item.from.name"
            :is-canceled="item.isCanceled"
            :is-deleted="item.isDeleted"
            @click="onClickBalloon(item)"
          />
        </div>
        <div
          :class="currentUser !== item.from.name ? 'date-other' : 'date-own'"
        >
          <template v-if="!item.isUnread && item.from.name !== currentUser">
            既読
          </template>
          {{ item.createdAt | humanize.date }}
        </div>
      </section>
    </div>

    <!-- メッセージ入力エリア -->
    <div class="MessageInputContainer" v-if="!ReadOnly">
      <!-- メッセージ入力 input -->
      <el-input
        class="MessageBodyInput"
        type="textarea"
        placeholder="メッセージを入力してください"
        v-model="body"
        :autosize="{ minRows: 3, maxRows: 6 }"
      ></el-input>

      <!-- メッセージ入力 ボタンエリア -->
      <div class="MessageInput-Buttons">
        <!-- 画像添付ボタン -->
        <el-upload
          class="AddAttachmentButton"
          ref="upload"
          action=""
          :auto-upload="false"
          :on-change="_onChangeAttachments"
          :file-list="fileList"
          accept=".jpg, .jpeg, .png, .doc, .xls, .ppt, .docx, .xlsx, .pptx, .pdf, .txt, .bmp"
          :on-remove="removeFile"
          :disabled="isUploadingImg"
        >
          <el-button
            size="small"
            type="default"
            :loading="isUploadingImg"
          >
            {{ isUploadingImg ? 'アップロード中' : 'ファイルを選択' }}
          </el-button>
          <div class="el-upload__tip" slot="tip">
            15MB以内
          </div>
        </el-upload>

        <!-- 送信ボタン -->
        <div class="MessageSendButtonContainer">
          <el-button
            size="small"
            class="MessageSendButton"
            type="primary"
            @click="onClickSendMessage"
            :loading="isSending"
            :disabled="isUploadingImg"
            >送信</el-button
          >
        </div>
      </div>
    </div>

    <!-- メッセージを削除 -->
    <el-dialog
      :visible.sync="isShowDialog"
      :show-close="false"
      :close-on-click-modal="!isDeleting"
      title="削除しますか？"
      width="35%"
    >
      <div class="dialog-text">
        {{ selectedMessage.body }}<br />
        <small>
          {{ selectedMessage.createdAt | humanize.date }}
        </small>
      </div>

      <!-- アクション -->
      <div
        slot="footer"
        class="dialog-footer"
      >
        <el-button
          plain
          :disabled="isDeleting"
          @click="isShowDialog = false"
        >
          キャンセル
        </el-button>
        <el-button
          type="danger"
          :loading="isDeleting"
          @click="onClickDelateMessage"
        >
          削除する
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import MAX_FILE_SIZE from "../../settings.js";

import AdminChatBaloon from "@/components/AdminChatBaloon.vue";

import UsecaseFactory from "@/usecases/usecase.factory.js";
import FileUsecase from "@/usecases/file.usecase";

import Auth from "@/services/auth.service";

const ALLOW_EXTS_IMG = ["jpg", "jpeg", "png", "bmp"];

export default {
  components: {
    AdminChatBaloon,
  },
  props: {
    ReadOnly: {
      type: Boolean,
      default: false,
    },
    /** 注文オブジェクト（親子解決用）。メッセージスレッドは常に親注文 ID。 */
    order: {
      type: Object,
      default: null,
    },
  },
  data () {
    return {
      isSending: false,
      isDeleting: false,
      messages: [],
      body: "",
      fileList: [],
      files: [],
      images: [],
      currentUser: "",
      admin: "窓口担当",
      isShowDialog: false,
      selectedMessage: {},
      isUploadingImg: false,
    };
  },
  watch: {
    messageThreadOrderId: {
      immediate: true,
      handler () {
        this.updateMessages();
      },
    },
  },
  computed: {
    MAX_FILE_SIZE () {
      return MAX_FILE_SIZE.MAX_FILE_SIZE;
    },
    /**
     * getMessages / sendMessage 用。子注文表示・印刷の子行でも親 ID に揃える。
     */
    messageThreadOrderId () {
      const routeId = this.$route.params.id
        ? String(this.$route.params.id)
        : "";
      if (!routeId) {
        return "";
      }
      const o = this.order;
      if (!o || !o.id) {
        return "";
      }
      if (String(o.id) === routeId) {
        if (o.parentOrder && o.parentOrder.id) {
          return String(o.parentOrder.id);
        }
        return String(o.id);
      }
      if (
        o.parentOrder &&
        o.parentOrder.id &&
        String(o.parentOrder.id) === routeId
      ) {
        return String(o.parentOrder.id);
      }
      return "";
    },
  },
  methods: {
    getExt (key) {
      var pos = key.lastIndexOf(".");
      if (pos === -1) return "";
      return key.slice(pos + 1);
    },
    checkExt (key) {
      // 拡張子を切り出す
      var ext = this.getExt(key);
      // 許可する画像拡張子かあるか確認
      if (ALLOW_EXTS_IMG.indexOf(ext) === -1) {
        return false;
      }
      return true;
    },

    /**
     * [_onChangeAttachments description]
     * @param   {[type]}  file      [file description]
     * @param   {[type]}  fileList  [fileList description]
     * @return  {[type]}            [return description]
     */
    async _onChangeAttachments (file, fileList) {
      const { raw, name } = file;

      // アップロード時のファイルサイズ制限
      // xxlarge: 15MB まで
      if (!usecase.file.validateFileSize(file, "xxlarge")) {
        // Pop file list
        fileList.pop();

        return;
      }

      // 保存する画像のカテゴリを分ける
      const category = "adminMessage";

      try {
        this.isUploadingImg = true;

        const fileName = usecase.file.fetchFileName(name, category);

        const result = await usecase.file.uploadFile(fileName, raw);

        this.fileList = fileList;

        const fileIsImg = this.checkExt(result.key);

        if (fileIsImg === true) {
          this.images.push(result.key);
        } else {
          this.files.push(result.key);
        }
      } catch (err) {
        console.log(err);

        // Pop file list
        fileList.pop();
      } finally {
        this.isUploadingImg = false;
      }
    },

    /**
     * [checkAndPushStoredFile description]
     * @param   {[type]}  file  [file description]
     * @return  {[type]}        [return description]
     */
    async checkAndPushStoredFile (file) {
      const { raw, name } = file;

      // アップロード済みのファイルを取得する形のためここではファイルサイズ制限の確認は行わなくて良い
      // if (!usecase.file.validateFileSize(file, "max")) {
      //   return;
      // }

      // 保存する画像のカテゴリ指定 + S3 へのオプション設定
      const category = "";
      const option = { contentType: raw.type };

      try {
        const fileName = usecase.file.fetchFileName(name, category);

        const result = await usecase.file.uploadFile(fileName, raw, option);

        const fileIsImg = this.checkExt(result.key);

        if (fileIsImg === true) {
          this.images.push(result.key);
        } else {
          this.files.push(result.key);
        }
      } catch (err) {
        console.log(err);
      }
    },

    /**
     * [removeFile description]
     * @param   {[type]}  file      [file description]
     * @param   {[type]}  fileList  [fileList description]
     * @return  {[type]}            [return description]
     */
    async removeFile (file, fileList) {
      this.images = [];
      this.files = [];

      for (var i = 0; i < fileList.length; i++) {
        await this.checkAndPushStoredFile(fileList[i]);
      }
    },

    initImageFiles () {
      this.$refs.upload.clearFiles();
      this.fileList = [];
    },

    async updateMessages () {
      const user = await authService.getCurrentUser();
      this.currentUser = user.attributes.name;
      const orderId = this.messageThreadOrderId;
      if (!orderId) {
        this.messages = [];
        this.$emit("has-messages", false);
        return;
      }
      const messages = await usecase.message.getMessages({ orderId });
      this.messages = messages;

      const hasMessages = this.messages && this.messages.length > 0;
      this.$emit("has-messages", hasMessages);

      this.markMessagesAsRead();
    },
    /**
     * メッセージを既読にする
     */
    markMessagesAsRead () {
      const { messages } = this;
      const unreadMessages = messages.filter(message => message.isUnread);

      Promise.all(unreadMessages.map((unreadMessage) => {
        const { id } = unreadMessage;
        usecase.message.markMessageAsRead({ id });
      }));
    },
    initMessageInput () {
      this.body = "";
      this.files = [];
      this.images = [];
      if (this.$refs.upload) {
        this.$refs.upload.clearFiles();
      }
    },
    async onClickSendMessage () {
      // Validation
      if (this.body === "") {
        alert("本文を入力してください");

        return;
      }

      const orderId = this.messageThreadOrderId;
      if (!orderId) {
        alert("注文情報の読み込みを待ってから再度お試しください");
        return;
      }
      const { body, files, images } = this;

      try {
        this.isSending = true;

        const sentMessage = await usecase.message.sendMessage({
          orderId,
          body,
          files,
          images,
        });

        // Cleanup
        if (sentMessage) {
          await this.initMessageInput();
          await this.updateMessages();
        }
      } catch (err) {
        console.log(err);

        // Failed to send
        alert("送信に失敗しました。再度お試しください");
      } finally {
        this.isSending = false;
      }
    },

    /**
     * [onClickBalloon description]
     * @param   {[type]}  message  [message description]
     * @return  {[type]}           [return description]
     */
    onClickBalloon (message) {
      if (message.isDeleted) {
        return;
      }

      this.selectedMessage = message;
      this.isShowDialog = true;
    },

    /**
     * [onClickDelateMessage description]
     * @return  {[type]}  [return description]
     */
    async onClickDelateMessage () {
      const { id } = this.selectedMessage;
      const option = { id };

      this.isDeleting = true;

      const res = await usecase.message.deleteMessage(option);

      this.isDeleting = false;

      if (res.errors) {
        res.errors.forEach(error => {
          this.$notify.error({
            title: "エラー",
            message: error.message,
            duration: 5 * 1000,
          });
        });

        return;
      }

      const { messageDelete } = res.data;

      this.$notify.success({
        title: "メッセージを削除しました",
        duration: 5 * 1000,
      });

      this.selectedMessage.isDeleted = messageDelete.isDeleted;
      this.selectedMessage.body = messageDelete.body;
      this.selectedMessage.images = messageDelete.images;
      this.selectedMessage.files = messageDelete.files;
      this.isShowDialog = false;
    },
  },

  /**
   * [beforeCreate description]
   */
  beforeCreate () {
    usecase.file = new FileUsecase();
    usecase.message = UsecaseFactory.create("message");
  },

  /**
   * [beforeDestroy description]
   */
  beforeDestroy () {
    Object.keys(usecase).map(key => {
      delete usecase[key];
    });
  },
};

const usecase = {};
const authService = new Auth();
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/variables.scss";

@media print {
  section {
    size: A4 portrait;
    margin: 15mm 10mm 15mm 10mm;
    page-break-after: auto;
  }

  .chat-window {
    display: flex;
    flex-direction: column;
    height: 735px;
  }

  .chat-messages {
    flex: 1;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column-reverse;
  }

  .MessageInputContainer {
    border-top: 1px solid $--border-color-lighter;
    padding: 10px;
  }

  .MessageInput-Buttons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    margin-top: 10px;
  }

  .MessageSendButtonContainer {
    text-align: right;
  }

  .bg-gray {
    background: $--border-color-extra-light;
    float: left;
    width: 55%;
  }

  .bg-blue {
    background: $blue-light;
    float: right;
    width: 55%;
  }

  .sender {
    margin-left: 1.3rem;
    margin-top: 1rem;
    font-size: 13px;
  }

  .date-own {
    width: 55%;
    line-height: 1;
    float: right;
    margin-right: 1.5rem;
    margin-bottom: 1.5rem;
    text-align: end;
    font-size: 13px;
  }

  .date-other {
    width: 55%;
    line-height: 1;
    float: left;
    margin-left: 1.2rem;
    margin-bottom: 1.5rem;
    text-align: end;
    font-size: 13px;
  }
}
@media screen {
  section {
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    page-break-after: auto;
  }
  .chat-window {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 735px;
  }

  .chat-messages {
    flex: 1;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column-reverse;
  }

  .MessageInputContainer {
    border-top: 1px solid $--border-color-lighter;
    padding: 10px;
  }

  .MessageInput-Buttons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    margin-top: 10px;
  }

  .MessageSendButtonContainer {
    text-align: right;
  }

  .bg-gray {
    background: $--border-color-extra-light;
    float: left;
    width: 55%;
  }

  .bg-blue {
    background: $blue-light;
    float: right;
    width: 55%;
  }

  .sender {
    margin-left: 1.3rem;
    margin-top: 1rem;
    font-size: 13px;
  }

  .date-own {
    width: 55%;
    line-height: 1;
    float: right;
    margin-right: 1.5rem;
    margin-bottom: 1.5rem;
    text-align: end;
    font-size: 13px;
  }

  .date-other {
    width: 55%;
    line-height: 1;
    float: left;
    margin-left: 1.2rem;
    margin-bottom: 1.5rem;
    text-align: end;
    font-size: 13px;
  }
}

// メッセージの削除ダイアログ
.chat-window {
  .dialog-text {
    margin: 1rem 0;
    font-size: 18px;
  }

  .dialog-footer {
    .el-button {
      border: none;
    }
  }
}
</style>
